/**
 * Pure converters between the on-the-wire `TaskSamplesView` and the runtime
 * `SamplesViewState`.
 *
 * No runtime imports — every function here is referentially transparent
 * given its inputs, so unit tests can exercise them in isolation.
 */
import type { SortingState } from "@tanstack/react-table";

import type { TaskSamplesView } from "@tsmono/inspect-common/types";

import { defaultSamplesView, type SamplesViewState } from "./samplesView";

// ---------------------------------------------------------------------------
// Runtime ↔ TanStack SortingState
// ---------------------------------------------------------------------------

/** `view.sort` → TanStack `SortingState` (the grid's controlled sort). */
export const viewSortToSorting = (
  sort: SamplesViewState["sort"]
): SortingState => sort.map((s) => ({ id: s.colId, desc: s.dir === "desc" }));

/** TanStack `SortingState` → `view.sort` (the persisted descriptor). */
export const sortingToViewSort = (
  sorting: SortingState
): SamplesViewState["sort"] =>
  sorting.map((s) => ({ colId: s.id, dir: s.desc ? "desc" : "asc" }));

// ---------------------------------------------------------------------------
// Wire ↔ runtime
// ---------------------------------------------------------------------------

/**
 * Lift a wire `TaskSamplesView` into the runtime `SamplesViewState`.
 *
 * - Nullable wire fields collapse to defaults from `defaultSamplesView()`.
 * - Sort entries map `column` → `colId` on the runtime side. Wire form
 *   keeps `column` to match the Python field.
 * - `extraColumnFilters` starts empty; residue is only synthesized at
 *   runtime when the user sets column-header filters the DSL can't express.
 */
export const liftEvalView = (
  wire: TaskSamplesView | undefined | null
): SamplesViewState => {
  const fallback = defaultSamplesView();
  if (!wire) return fallback;
  return {
    name: wire.name,
    columns: wire.columns
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        wire.columns.map((c) => ({ id: c.id, visible: c.visible ?? true }))
      : fallback.columns,
    sort: wire.sort
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        wire.sort.map((s) => ({ colId: s.column, dir: s.dir ?? "asc" }))
      : fallback.sort,
    filters: { dsl: "", extraColumnFilters: {} },
    multiline: wire.multiline ?? fallback.multiline,
    compactScores: wire.compact_scores ?? fallback.compactScores,
    colorScalesEnabled:
      wire.color_scales_enabled ?? fallback.colorScalesEnabled,
    userOverrides: {},
  };
};

/**
 * Flatten a runtime state back to its wire shape. `extraColumnFilters` is
 * dropped — that field is TS-only and never crosses the wire. Used to
 * compare a stored view against an eval default for override detection.
 */
export const flattenToEvalView = (
  state: SamplesViewState
): TaskSamplesView => ({
  name: state.name,
  columns: state.columns.map((c) => ({ id: c.id, visible: c.visible })),
  sort: state.sort.map((s) => ({ column: s.colId, dir: s.dir })),
  multiline: state.multiline,
  compact_scores: state.compactScores,
  color_scales_enabled: state.colorScalesEnabled,
});

/**
 * `task_samples_view` may be a single view, a list of views, or absent.
 * For Phase 0–2 we honour only the first entry of a list.
 */
export const pickActiveView = (
  field: TaskSamplesView | TaskSamplesView[] | undefined | null
): TaskSamplesView | undefined => {
  if (!field) return undefined;
  if (Array.isArray(field)) return field[0];
  return field;
};

/**
 * Resolution priority: stored (user-modified runtime state) > eval default
 * (lifted from `eval.viewer.task_samples_view`) > built-in default.
 *
 * Per-field exception for the toolbar toggles (`multiline`, `compactScores`,
 * `colorScalesEnabled`): stored only wins if `userOverrides` records that
 * the user has actually flipped that toggle. Otherwise the eval-author
 * default takes precedence even when stored state already exists, so an
 * eval that ships e.g. `color_scales_enabled: false` isn't shadowed by a
 * stale `true` lifted from a previous eval.
 */
export const resolveSamplesView = (
  stored: SamplesViewState | undefined,
  evalDefault: TaskSamplesView | undefined | null
): SamplesViewState => {
  if (!stored) return liftEvalView(evalDefault);
  const lifted = liftEvalView(evalDefault);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const overrides = stored.userOverrides ?? {};
  return {
    ...stored,
    multiline: overrides.multiline ?? lifted.multiline,
    compactScores: overrides.compactScores ?? lifted.compactScores,
    colorScalesEnabled:
      overrides.colorScalesEnabled ?? lifted.colorScalesEnabled,
  };
};

/**
 * Replace `view.columns[].visible` from a `(colId → visible)` map without
 * touching column order. Use when the column-selector popover hands back
 * a visibility map (e.g. ColumnSelectorPopover). Unknown ids in the map
 * are ignored; ids in the view but absent from the map are left as-is.
 */
export const mergeColumnVisibility = (
  view: SamplesViewState,
  visibility: Record<string, boolean>
): SamplesViewState => ({
  ...view,
  columns: view.columns.map((c) => {
    const nextVisible = visibility[c.id];
    return nextVisible !== undefined ? { ...c, visible: nextVisible } : c;
  }),
});
